import { Injectable } from '@angular/core';
import { LoggerService } from 'src/app/core/services/logger.service';
import { LogEvent } from 'src/app/core/enums/log-event';
import { EmojiSuggestion } from '../../shared/components/emoji-suggestion/emoji-suggestion.interface';
import { StripHtmlHelper } from 'src/app/shared/helpers/strip-html.helper';
import { EmojiSearch } from '@ctrl/ngx-emoji-mart';

// Interface para os objetos emoji da biblioteca
interface EmojiObject {
  native?: string;
  colons?: string;
  unified?: string;
  keywords?: string[];
  name?: string;
}
import emojiPT from '@assets/i18n/emoji-pt.json';
import emojiES from '@assets/i18n/emoji-es.json';

@Injectable({
  providedIn: 'root',
})
export class EmojiService {
  constructor(
    private logger: LoggerService,
    private stripHtmlHelper: StripHtmlHelper,
    private emojiSearch: EmojiSearch
  ) {}

  public logEmojiPickerOpened(): void {
    this.logger.log(LogEvent.LIVECHAT_OPEN_EMOJI_PICKER);
  }

  public logEmojiSuggestionOpened(): void {
    this.logger.log(LogEvent.LIVECHAT_OPEN_EMOJI_SUGGESTION);
  }

  public handleEmojiSuggestionCommand({
    text,
    inputCursorPosition,
  }: {
    text: string;
    inputCursorPosition: number;
  }) {
    const textWithoutHtml = this.stripHtmlHelper.stripHtmlAndNormalize(text);
    const regex = /:([A-Za-z0-9À-ÿ_]{3,}):?/g;
    let match: RegExpMatchArray | null;
    let foundMatch = null;
    const cursor = inputCursorPosition;

    while ((match = regex.exec(textWithoutHtml)) !== null) {
      const start = match.index || 0;
      const end = (match.index || 0) + match[0].length;

      if (cursor >= start && cursor <= end) {
        foundMatch = match;
        break;
      }
    }

    return foundMatch;
  }

  public searchSuggestion({ emojiTarget }: { emojiTarget: string }) {
    const results = this.emojiSearch.search(emojiTarget) || [];
    return (
      results.map((o: EmojiObject) => {
        return {
          emoji: o.native || '',
          colons: o.colons || '',
        };
      }) || []
    );
  }

  public extractEmojiMatch(
    text: string
  ): { match: RegExpMatchArray; colons: string } | null {
    const emojiMatches = text.match(/(?::)([a-zA-Z0-9_\-+]+)(?:$|:)/);
    if (!emojiMatches) {
      return null;
    }

    return {
      match: emojiMatches,
      colons: emojiMatches[0],
    };
  }

  public chooseLanguageEmoji(selectedLanguage: string): void {
    if (selectedLanguage === 'en') return;

    const emojisI18N = {
      pt: emojiPT,
      es: emojiES,
    };

    const emojiList =
      emojisI18N[selectedLanguage as keyof typeof emojisI18N] || [];

    for (const emojiTranslated of emojiList) {
      const entries = Object.entries(this.emojiSearch.emojisList);
      const foundEntry = entries.find((entry) => {
        const emojiValue = entry[1] as EmojiObject;
        return emojiValue.unified === emojiTranslated.unified;
      });
      const key = foundEntry?.[0];
      if (!key) continue;
      if (this.emojiSearch.emojisList[key]) {
        this.emojiSearch.emojisList[key].keywords = emojiTranslated.keywords;

        this.emojiSearch.emojisList[
          key
        ].colons = `:${emojiTranslated.shortName}:`;
      }
    }

    Object.keys(this.emojiSearch.emojisList).forEach((key) => {
      this.emojiSearch.emojiSearch[key] =
        this.emojiSearch.emojisList[key].keywords.join(',');
    });
  }

  public replaceEmojiInText(
    text: string,
    emojiCode: string,
    emoji: string
  ): string {
    return text.replace(emojiCode, emoji);
  }

  public getNewCursorPosition(
    originalPosition: number,
    emojiLength: number,
    codeLength: number
  ): number {
    return originalPosition + emojiLength - codeLength;
  }

  public formatHtmlWithEmoji(text: string, emoji: string): string {
    if (text?.startsWith('<p>') && text?.endsWith('</p>')) {
      const textWithoutHtml = this.stripHtml(text);
      return `<p>${textWithoutHtml}${emoji}</p>`;
    }
    return '';
  }

  private stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
}
